import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import {
  DeviceMetaEntry,
  DeviceMetaMap,
} from "../../../../service/models/complaint_model";
import { DeviceIdRegex } from "../../../../utils/constant";

export interface DeviceVerifyResult {
  verified: boolean;
  reason?: string;
  details?: Partial<DeviceMetaEntry>;
}

interface DeviceTabsProps {
  devices: string[];
  deviceMeta: DeviceMetaMap;
  editable: boolean;
  workingStatusRequired?: boolean;
  // when false, hides the in-tab "add device" input + chips (devices are added
  // elsewhere, e.g. the top Registered Mobile / Device ID lookup)
  showAddInput?: boolean;
  // suggested linked devices (e.g. from a mobile lookup) shown as quick-add chips
  linkedDevices?: { id: string; label?: string }[];
  // page-supplied verification (reuses the existing Home Platform lookup)
  verifyDevice?: (deviceId: string) => Promise<DeviceVerifyResult>;
  onChange: (next: { devices: string[]; deviceMeta: DeviceMetaMap }) => void;
  // UBC devices use ids like "<id>_1" for sub-devices; skip the standard
  // 6-char device id regex for those complaints.
  skipDeviceIdValidation?: boolean;
}

const WORKING_STATUS_OPTIONS = ["Working", "Partially Working", "Not Working"];

const normalise = (id: string) => id.trim().toLowerCase();

// Kazam standard 1-Year Replacement Warranty from installation date.
const WARRANTY_MONTHS = 12;

const fmtDate = (d: Date): string =>
  d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatInstallDate = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return fmtDate(d);
};

// Derived warranty details from the installation date. Returns null when the
// date is unknown/invalid.
const getWarranty = (
  value?: string,
): { underWarranty: boolean; endDate: string } | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const expiry = new Date(d);
  expiry.setMonth(expiry.getMonth() + WARRANTY_MONTHS);
  return {
    underWarranty: new Date().getTime() <= expiry.getTime(),
    endDate: fmtDate(expiry),
  };
};

const DeviceTabs: FunctionComponent<DeviceTabsProps> = ({
  devices,
  deviceMeta,
  editable,
  workingStatusRequired,
  showAddInput = true,
  linkedDevices = [],
  verifyDevice,
  onChange,
  skipDeviceIdValidation = false,
}) => {
  const { showToast } = useToast();
  const [active, setActive] = useState(0);
  const [newDeviceId, setNewDeviceId] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (active > devices.length - 1) setActive(Math.max(0, devices.length - 1));
  }, [devices.length, active]);

  const addableLinked = useMemo(
    () => linkedDevices.filter((d) => !devices.includes(normalise(d.id))),
    [linkedDevices, devices],
  );

  const emitChange = (nextDevices: string[], nextMeta: DeviceMetaMap) =>
    onChange({ devices: nextDevices, deviceMeta: nextMeta });

  const addDevice = async (rawId: string) => {
    const id = normalise(rawId);
    if (!id) return;
    if (!skipDeviceIdValidation && !DeviceIdRegex.test(id)) {
      showToast({
        type: ToastType.warning,
        text: `Device id "${id}" is not valid`,
      });
      return;
    }
    if (devices.includes(id)) {
      showToast({
        type: ToastType.warning,
        text: `Device "${id}" is already added`,
      });
      setActive(devices.indexOf(id));
      return;
    }

    let entry: DeviceMetaEntry = { verified: true };
    if (verifyDevice) {
      setAdding(true);
      try {
        const result = await verifyDevice(id);
        entry = {
          ...(result.details ?? {}),
          verified: result.verified,
          ...(result.verified ? {} : { verify_failed_reason: result.reason }),
        };
        if (!result.verified) {
          // Keep the device, just warn — failed devices are not dropped.
          showToast({
            type: ToastType.warning,
            text: `Device "${id}" could not be verified: ${
              result.reason ?? "not found"
            }. Added anyway.`,
          });
        }
      } catch (e: any) {
        entry = {
          verified: false,
          verify_failed_reason: e?.message ?? "Verification failed",
        };
        showToast({
          type: ToastType.warning,
          text: `Device "${id}" verification errored. Added anyway.`,
        });
      } finally {
        setAdding(false);
      }
    }

    const nextDevices = [...devices, id];
    const nextMeta = { ...deviceMeta, [id]: entry };
    emitChange(nextDevices, nextMeta);
    setNewDeviceId("");
    setActive(nextDevices.length - 1);
  };

  const removeDevice = (id: string) => {
    const nextDevices = devices.filter((d) => d !== id);
    const nextMeta = { ...deviceMeta };
    delete nextMeta[id];
    emitChange(nextDevices, nextMeta);
  };

  const patchEntry = (id: string, patch: Partial<DeviceMetaEntry>) => {
    const nextMeta = {
      ...deviceMeta,
      [id]: { ...(deviceMeta[id] ?? {}), ...patch },
    };
    emitChange(devices, nextMeta);
  };

  const activeId = devices[active];
  const activeEntry: DeviceMetaEntry = activeId
    ? (deviceMeta[activeId] ?? {})
    : {};

  return (
    <div className="border rounded-md">
      {editable && showAddInput && (
        <div className="p-3 border-b bg-gray-50 space-y-2">
          <div className="flex gap-2">
            <input
              id="device_tabs_new_device_id"
              type="text"
              value={newDeviceId}
              disabled={adding}
              onChange={(e) => setNewDeviceId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDevice(newDeviceId);
                }
              }}
              placeholder="Enter device ID and press Enter or click Add"
              className="border px-2 py-2 text-sm rounded flex-1"
            />
            <button
              id="device_tabs_add_device_btn"
              type="button"
              disabled={adding || !newDeviceId.trim()}
              onClick={() => addDevice(newDeviceId)}
              className="px-3 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50 whitespace-nowrap"
            >
              {adding ? "Verifying…" : "Add device"}
            </button>
          </div>
          {addableLinked.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-gray-500">Linked devices:</span>
              {addableLinked.map((d) => (
                <button
                  id={`device_tabs_linked_device_${d.id}_add_btn`}
                  key={d.id}
                  type="button"
                  disabled={adding}
                  onClick={() => addDevice(d.id)}
                  className="text-xs px-2 py-1 rounded-full border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                >
                  + {d.label ?? d.id}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {devices.length === 0 ? (
        <div className="p-3 text-sm text-gray-500">
          No devices added{editable ? " yet." : "."}
        </div>
      ) : (
        <>
          {/* tab strip — horizontally scrollable on small screens */}
          <div className="flex gap-1 overflow-x-auto border-b bg-white px-2 pt-2">
            {devices.map((id, idx) => {
              const meta = deviceMeta[id] ?? {};
              const failed = meta.verified === false;
              const wcrMissing = !failed && meta.wcr_present === false;
              return (
                <button
                  id={`device_tabs_tab_${id}`}
                  key={id}
                  type="button"
                  onClick={() => setActive(idx)}
                  className={`flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm rounded-t border-t border-l border-r -mb-px ${
                    idx === active
                      ? "bg-white border-gray-300 font-medium"
                      : "bg-gray-50 border-transparent text-gray-500"
                  }`}
                >
                  <span>{id}</span>
                  <span
                    title={
                      failed
                        ? (meta.verify_failed_reason ?? "Not verified")
                        : wcrMissing
                          ? "Installed — WCR not filled on Home App yet"
                          : "Verified"
                    }
                    className={
                      failed
                        ? "text-red-500"
                        : wcrMissing
                          ? "text-amber-500"
                          : "text-green-600"
                    }
                  >
                    {failed ? "✗" : wcrMissing ? "⚠" : "✓"}
                  </span>
                </button>
              );
            })}
          </div>

          {activeId && (
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{activeId}</div>
                {editable && (
                  <button
                    id={`device_tabs_tab_${activeId}_remove_btn`}
                    type="button"
                    onClick={() => removeDevice(activeId)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove device
                  </button>
                )}
              </div>

              {/* verification summary */}
              {activeEntry.verified === false ? (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                  ✗ Not verified
                  {activeEntry.verify_failed_reason
                    ? `: ${activeEntry.verify_failed_reason}`
                    : ""}
                </p>
              ) : activeEntry.wcr_present === false ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠ Installed — WCR not filled on Home App yet. A ticket can
                  still be created for this device.
                </p>
              ) : (
                <p className="text-xs text-green-700">✓ Verified</p>
              )}

              {activeEntry.installation_date &&
                formatInstallDate(activeEntry.installation_date) && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-gray-600">
                      Installation Date:{" "}
                      <span className="font-medium text-gray-800">
                        {formatInstallDate(activeEntry.installation_date)}
                      </span>
                    </span>
                    {(() => {
                      const w = getWarranty(activeEntry.installation_date);
                      if (!w) return null;
                      return (
                        <span
                          className={`rounded-full px-2 py-0.5 ${
                            w.underWarranty
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                          title={`Warranty ${
                            w.underWarranty ? "valid till" : "expired on"
                          } ${w.endDate}`}
                        >
                          {w.underWarranty
                            ? `Under Warranty · valid till ${w.endDate}`
                            : `Out of Warranty · expired ${w.endDate}`}
                        </span>
                      );
                    })()}
                  </div>
                )}

              <div className="flex flex-col">
                <label className="text-sm leading-7">
                  Device working status
                  {workingStatusRequired ? " *" : ""}
                </label>
                <select
                  id="device_tabs_working_status"
                  disabled={!editable}
                  required={workingStatusRequired}
                  value={activeEntry.working_status || ""}
                  onChange={(e) =>
                    patchEntry(activeId, { working_status: e.target.value })
                  }
                  className="border px-2 py-2 text-sm rounded disabled:bg-gray-50"
                >
                  <option value="">Select status</option>
                  {WORKING_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm leading-7">Device remark</label>
                <textarea
                  id="device_tabs_remark"
                  disabled={!editable}
                  value={activeEntry.remark || ""}
                  onChange={(e) =>
                    patchEntry(activeId, { remark: e.target.value })
                  }
                  rows={2}
                  className="border px-2 py-2 text-sm rounded disabled:bg-gray-50"
                  placeholder="Optional remark specific to this device"
                />
              </div>

              {activeEntry.oem_service_ticket_id && (
                <div className="flex flex-col">
                  <label className="text-sm leading-7">Service Ticket ID</label>
                  <input
                    disabled
                    value={activeEntry.oem_service_ticket_id}
                    className="border px-2 py-1 text-sm bg-gray-50 text-gray-500 rounded"
                  />
                </div>
              )}

              {activeEntry.home_installation_id && (
                <div className="flex flex-col">
                  <label className="text-sm leading-7">Installation ID</label>
                  <input
                    disabled
                    value={activeEntry.home_installation_id}
                    className="border px-2 py-1 text-sm bg-gray-50 text-gray-500 rounded"
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceTabs;
